/**
 * SectionErrorBoundary — XCUT-13.
 *
 * A compact, retryable React error boundary for wrapping an INDEPENDENT
 * dashboard section so that one card's render throw is contained to that card
 * rather than tearing down the whole app (the existing `RootErrorBoundary` in
 * `main.tsx` + the router `errorComponent` are last-line whole-screen
 * fallbacks; this is the in-place, per-section one).
 *
 * The fallback is honest and reassuring ("this section couldn't load — your
 * data is safe") and offers a local retry that re-mounts the wrapped subtree
 * (bumping an internal key) without a full page reload. Light theme, inline
 * styles + design-system CSS variables only, so it has no dependency that could
 * itself be the thing that threw.
 *
 * Scope note: this is deliberately focused on the key independent dashboard
 * cards, not an exhaustive every-component wrap.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface SectionErrorBoundaryProps {
  children: ReactNode;
  /** Optional label shown in the fallback (e.g. "Allocation"). */
  label?: string;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
  /** Bumped on retry to force a fresh mount of `children`. */
  retryKey: number;
}

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryKey: 0 };
  }

  static getDerivedStateFromError(): Partial<SectionErrorBoundaryState> {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Triage log only — never render the raw error to the user (no stack /
    // infra leak). A telemetry sink can subscribe to console later.
    console.error(
      `Section "${this.props.label ?? 'unknown'}" failed to render:`,
      error,
      info.componentStack,
    );
  }

  private handleRetry = (): void => {
    // Clear the error AND re-mount the subtree so a transient failure (e.g. a
    // one-off render race) gets a clean second chance.
    this.setState((s) => ({ hasError: false, retryKey: s.retryKey + 1 }));
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return <SectionErrorFallback label={this.props.label} onRetry={this.handleRetry} />;
    }
    // Keying on retryKey forces React to discard the old subtree on retry.
    return <div key={this.state.retryKey}>{this.props.children}</div>;
  }
}

/**
 * Compact in-card fallback for {@link SectionErrorBoundary}. `role="alert"` so
 * assistive tech announces it. Exported so tests can assert its content.
 */
export function SectionErrorFallback({
  label,
  onRetry,
}: {
  label?: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      data-testid="section-error-fallback"
      className="card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
        padding: '24px 28px',
      }}
    >
      <div>
        <div
          className="eyebrow"
          style={{ marginBottom: 6 }}
        >
          {label ? `${label} unavailable` : 'Section unavailable'}
        </div>
        <p
          style={{
            fontSize: 14,
            lineHeight: 1.55,
            color: 'var(--ink-2, #3a3a3a)',
            margin: 0,
          }}
        >
          This section couldn&rsquo;t load. Your data is safe and nothing
          has been changed. You can retry without leaving the page.
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        style={{
          background: 'transparent',
          color: 'var(--primary-700)',
          border: '1px solid var(--primary-200, #b8d4ce)',
          padding: '7px 16px',
          borderRadius: 8,
          fontFamily: 'var(--sans, system-ui, sans-serif)',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        Retry
      </button>
    </div>
  );
}
